/**
 * creature/state.ts — A Creature in the World
 *
 * The runtime state of a single creature instance. Everything the game
 * needs to know to update, render, and interact with a living creature.
 *
 * Mirrors the pattern of PlayerState in engine/state.ts:
 * position, direction, animation, inventory of accessories.
 * But richer — creatures have needs, genomes, bonds, and genetics.
 */

import type { Direction } from "../engine/state.js";
import type { SkeletonInstance } from "./skeleton.js";
import { createSkeletonInstance } from "./skeleton.js";
import type { CreaturePose } from "./pose.js";
import { zeroPose, clonePose } from "./pose.js";
import type { BehaviorGenome } from "./genome.js";
import { createGenome } from "./genome.js";
import type { BehaviorState, CreatureNeeds } from "./behavior.js";
import { defaultBehaviorState, defaultNeeds } from "./behavior.js";
import type { SpeciesTemplate } from "./species.js";
import { getSpecies } from "./species.js";

// ─── Bond Memory ────────────────────────────────────────────────────────────

export interface BondMemory {
  type: "first_meeting" | "fed" | "played" | "named" | "adventure" | "bred" | "healed" | "groomed";
  timestamp: number;
  detail?: string;
}

// ─── Mount State ────────────────────────────────────────────────────────────

export interface MountState {
  riderId: string;
  mounted: boolean;
  mountTime: number;
}

// ─── Creature Genetics ──────────────────────────────────────────────────────

export interface CreatureGenetics {
  /** Diploid genes: each is a pair of alleles (0-1 floats) */
  genes: Record<string, [number, number]>;
}

// ─── Creature State ─────────────────────────────────────────────────────────

export interface CreatureState {
  id: string;
  speciesId: string;
  name: string;
  ownerId: string | null;

  // Position
  position: { tileX: number; tileY: number; elevation: number };
  direction: Direction;
  targetPosition?: { tileX: number; tileY: number };
  movementQueue: Array<{ tileX: number; tileY: number }>;
  moveProgress: number;
  walkDir: number;   // -1 or 1

  // Animation
  skeleton: SkeletonInstance;
  pose: CreaturePose;
  targetPose: CreaturePose;

  // Behavior
  behaviorState: BehaviorState;
  genome: BehaviorGenome;

  // Needs
  needs: CreatureNeeds;

  // Appearance
  palette: Record<string, string>;
  accessories: string[];

  // Bond
  bondLevel: number;
  bondMemories: BondMemory[];

  // Genetics
  genetics: CreatureGenetics;

  // Mount
  mountState: MountState | null;
}

// ─── Factory ────────────────────────────────────────────────────────────────

let nextId = 1;

/** Create a new creature instance from a species template */
export function createCreature(
  speciesId: string,
  position: { tileX: number; tileY: number; elevation: number },
  options?: {
    name?: string;
    ownerId?: string | null;
    palette?: Record<string, string>;
  }
): CreatureState | null {
  const species = getSpecies(speciesId);
  if (!species) return null;

  const id = `creature_${nextId++}`;
  const genome = createGenome(species.behaviorDefaults);

  return {
    id,
    speciesId,
    name: options?.name ?? species.displayName,
    ownerId: options?.ownerId ?? null,

    position: { ...position },
    direction: "S",
    movementQueue: [],
    moveProgress: 0,
    walkDir: 1,

    skeleton: createSkeletonInstance(species.skeleton),
    pose: zeroPose(),
    targetPose: zeroPose(),

    behaviorState: defaultBehaviorState(),
    genome,

    needs: defaultNeeds(),

    palette: options?.palette ?? { ...species.palette },
    accessories: [],

    bondLevel: 0,
    bondMemories: [],

    genetics: createDefaultGenetics(genome),

    mountState: null,
  };
}

// ─── Genetics Factory ───────────────────────────────────────────────────────

function createDefaultGenetics(genome: BehaviorGenome): CreatureGenetics {
  const genes: Record<string, [number, number]> = {};

  // Behavior genes — each allele is the trait value with small jitter
  for (const [key, value] of Object.entries(genome)) {
    const jitter = (Math.random() - 0.5) * 0.1;
    genes[`behavior_${key}`] = [
      Math.max(0, Math.min(1, value + jitter)),
      Math.max(0, Math.min(1, value - jitter)),
    ];
  }

  // Appearance genes (will be used by breeding to determine offspring colors)
  genes["hue_body"] = [Math.random(), Math.random()];
  genes["hue_markings"] = [Math.random(), Math.random()];
  genes["saturation"] = [Math.random(), Math.random()];
  genes["ear_size"] = [Math.random(), Math.random()];
  genes["tail_length"] = [Math.random(), Math.random()];
  genes["leg_length"] = [Math.random(), Math.random()];

  return { genes };
}
